import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSparks } from '../../contexts/SparksContext';
import LessonRecap from '../../components/Sparks/LessonRecap';
import SparksFinishHold from '../../components/Sparks/SparksFinishHold';
import useBeginLessonTally from '../../components/Sparks/useBeginLessonTally';
import SparksStarsUpdate from '../../components/Sparks/SparksStarsUpdate';
import useEarnedToday, { isHomeRepeat, isWorksheetRepeat, REPEAT_COPY } from '../../components/Sparks/earnedToday';
import { useParams, useLocation, useNavigate } from "react-router-dom";
import QuestionPage from "../../components/Quiz/QuestionPage/QuestionPage";
import { MaybeZoom } from "../../components/Quiz/QuestionZoom/QuestionZoom";
import { useInClassroom } from "../../contexts/InClassroomContext";
import { useNavVisibility } from "../../contexts/NavVisibilityContext";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
// import ProgressBar from "../../components/ProgressBar/ProgressBar";
import { get, patch, post, put, del } from '../../utils/api';
import { completeCoreSkillSession } from '../../api/dashboardApi';
import { useAuth } from '../../contexts/AuthContext';
import posthog from 'posthog-js';
// #import { getRandomMusicFile } from "../../utils/musicUtils";
import "./Quiz.css";

const QuizPage = ({
    quizSubmissionId: propQuizSubmissionId,
    lessonId: propLessonId,
    topicId: propTopicId,
    onQuizComplete,
    isCoreSkill = false,
    preventNavigation,
    onStateChange,
    onInputChange,
    onTimerSync,
    isTutorPreview = false,
    previewState = null,
    isWorksheet = false,
    worksheetSubmissionId: propWorksheetSubmissionId = null
}) => {
    const { topicId: routeTopicId, lessonId: routeLessonId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user, loggedInChild } = useAuth();
    // Sparks: every activity reply carries `sparksAwarded` / `sparksBalance` (design §2.6);
    // the context draws the "+N" beside the balance chip. Never computed client-side.
    const { pushFromResponse, pushStreakResult } = useSparks();
    const earnedToday = useEarnedToday(isTutorPreview ? null : loggedInChild?._id);
    // True only when the quiz is rendered inside a live classroom call.
    const inClassroom = useInClassroom();

    // Fires after any qualifying completion — the streak chip and celebration cards update live
    const refreshStreak = () => {
        if (!loggedInChild || isTutorPreview) return;
        post(`/children/${loggedInChild._id}/streak-activity`, {}, true)
            .then(pushStreakResult)
            .catch(() => {}); // non-fatal — the chip updates on next child select if this fails
    };

    const [quizSubmission, setQuizSubmission] = useState(null);
    const [remainingSeconds, setRemainingSeconds] = useState(null);
    const [quizQuestions, setQuizQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [loading, setLoading] = useState(!isTutorPreview);
    const [quizStarted, setQuizStarted] = useState(!!(isTutorPreview && previewState?.quizSubmission));
    const [allQuestionAttempts, setAllQuestionAttempts] = useState([]);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [ButtonsAreClickable, setButtonsAreClickable] = useState(true);
    const [musicFile, setMusicFile] = useState(null);

    // State for explanation questions
    const [showingExplanation, setShowingExplanation] = useState(false);
    const [currentExplanationQuestion, setCurrentExplanationQuestion] = useState(null);

    // State for controlling Next button and feedback
    const [showNextButton, setShowNextButton] = useState(false);
    const [showFeedback, setShowFeedback] = useState(true);

    // Add state for resetting feedback
    const [resetFeedback, setResetFeedback] = useState(false);

    const [preQuizResult, setPreQuizResult] = useState(null);

    const actualQuizSubmissionId = isWorksheet
        ? propWorksheetSubmissionId
        : (preventNavigation ? propQuizSubmissionId : location.state?.quizSubmissionId);
    const actualTopicId = preventNavigation ? propTopicId : routeTopicId;
    const actualLessonId = preventNavigation ? propLessonId : routeLessonId;
    const dashboardSessionId = location.state?.dashboardSessionId;
    const coreSkillSource = location.state?.coreSkillSource;

    const quizFinishedMessage = preventNavigation ? false : true;

    // Stable reference for the question currently shown to the student.
    // Without this, quizQuestions[currentQuestionIndex] produces a new object reference
    // on every render of QuizPage (e.g. triggered by a parent re-render), which causes
    // QuestionPage's input-reset useEffect — which depends on quizQuestion — to fire
    // mid-keystroke and wipe the student's typed answer.
    // This only recomputes when the index, the questions array, or the explanation state
    // actually changes — not on every parent re-render.
    const currentQuestion = useMemo(
        () => showingExplanation ? currentExplanationQuestion : quizQuestions[currentQuestionIndex],
        [showingExplanation, currentExplanationQuestion, currentQuestionIndex, quizQuestions]
    );

    // A quiz question is on screen (not the start screen, completion screen, or
    // tutor preview). Drives the immersive nav-hide in ALL contexts — classroom,
    // home learning, Core Skills, worksheets, homework, standalone. State-driven
    // so the nav reliably returns when the question is gone.
    const { setNavHidden } = useNavVisibility();
    const questionActive =
        !isTutorPreview && quizStarted &&
        quizSubmission?.QuizStatus !== 'Complete' && quizQuestions.length > 0;
    useEffect(() => {
        setNavHidden(questionActive);
        return () => setNavHidden(false);
    }, [questionActive, setNavHidden]);
    // Opens the recap's tally when the child comes straight to questions (practice, homework, worksheet, pre-quiz).
    useBeginLessonTally(loggedInChild?._id, questionActive);

    // Outside a live call the nav is hidden and there is no Leave-call button, so
    // offer an Exit (confirm first; progress is saved as the student answers).
    const handleExit = () => {
        confirmAlert({
            title: 'Exit?',
            message: 'Your progress is saved.',
            buttons: [
                { label: 'Exit', onClick: () => navigate('/children-dashboard') },
                { label: 'Stay', onClick: () => {} },
            ],
        });
    };

    // When tutor preview: hydrate from synced student state; no fetch.
    // Build quizQuestions from submission order (all attempts). Resolve display index by
    // current question ID from student so tutor always shows the same question.
    useEffect(() => {
        if (!isTutorPreview || !previewState?.quizSubmission) return;
        const sub = previewState.quizSubmission;
        setQuizSubmission(sub);
        const allInOrder = (sub.questionAttempts || [])
            .map(attempt => attempt.LessonQuestionId);
        setQuizQuestions(allInOrder);
        setTotalQuestions((sub.questionAttempts || []).length);
        setAllQuestionAttempts(sub.questionAttempts || []);
        const targetId = previewState.currentQuestionId != null && typeof previewState.currentQuestionId === 'object'
            ? previewState.currentQuestionId._id
            : previewState.currentQuestionId;
        const resolvedIndex = targetId != null
            ? allInOrder.findIndex(q => (q && (typeof q === 'object' ? q._id : q)) === targetId)
            : -1;
        setCurrentQuestionIndex(
            resolvedIndex >= 0 ? resolvedIndex : (typeof previewState.currentQuestionIndex === 'number' ? previewState.currentQuestionIndex : 0)
        );
        setShowingExplanation(!!previewState.showingExplanation);
        setCurrentExplanationQuestion(previewState.currentExplanationQuestion ?? null);
        setLoading(false);
        setQuizStarted(true);
    }, [isTutorPreview, previewState?.quizSubmission, previewState?.currentQuestionIndex, previewState?.currentQuestionId, previewState?.showingExplanation, previewState?.currentExplanationQuestion]);

    // The load reads the parent's callback and the child through refs. A parent re-render (every
    // Sparks award re-renders LearningSession) hands down a new onQuizComplete; reloading on that
    // reset the question list to the unanswered ones while the index stayed put, skipping
    // questions and then running past the end, which blanked the screen.
    const onQuizCompleteRef = useRef(onQuizComplete);
    onQuizCompleteRef.current = onQuizComplete;
    const loggedInChildRef = useRef(loggedInChild);
    loggedInChildRef.current = loggedInChild;

    useEffect(() => {
        if (isTutorPreview) return;
        const fetchQuizSubmission = async () => {
            const onQuizComplete = onQuizCompleteRef.current;
            const loggedInChild = loggedInChildRef.current;
            try {
                const url = isWorksheet
                    ? `/worksheets/submission/${actualQuizSubmissionId}`
                    : `/quiz/quiz-submissions/${actualQuizSubmissionId}`;
                const response = await get(url, true);

                if (!response) {
                    console.error('No data returned from API');
                    return;
                }

                if (response.questionAttempts.length === 0) {
                    const updatedQuizSubmission = {
                        ...response,
                        QuizStatus: "Complete",
                        completionDate: new Date()
                    };
                    const strippedData = isWorksheet
                        ? stripWorksheetData(updatedQuizSubmission)
                        : stripPopulatedData(updatedQuizSubmission);
                    await patch(
                        isWorksheet ? `/worksheets/${response._id}` : `/quiz/quiz-submissions/${response._id}`,
                        strippedData,
                        true
                    );
                    setQuizSubmission(updatedQuizSubmission);

                    if (preventNavigation && onQuizComplete) {
                        onQuizComplete(updatedQuizSubmission);
                    } else if (!isWorksheet && loggedInChild) {
                        if (!response.isCoreSkill) {
                            try {
                                await put(`/children/${loggedInChild._id}/current-home-topic`, { topicId: null }, true);
                                await del(`/childTopics/${loggedInChild._id}/${actualTopicId}?track=${response.track}`, true);
                            } catch (error) {
                                console.error('Error clearing current topic or deleting ChildTopic entry:', error);
                            }
                        }
                    }
                } else {
                    setQuizSubmission(response);
                    setAllQuestionAttempts(response.questionAttempts);
                    setQuizQuestions(response.questionAttempts
                        .filter(attempt => attempt.QuestionStatus === "not attempted")
                        .map(attempt => attempt.LessonQuestionId)
                    );
                    if (response.timerEnabled != null && response.remainingSeconds != null) {
                        setRemainingSeconds(response.remainingSeconds);
                    }
                }
                setTotalQuestions(response.questionAttempts.length);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching quiz submission:', error);
                setLoading(false);
            }
        };

        if (actualQuizSubmissionId) {
            fetchQuizSubmission();
        }
    }, [actualQuizSubmissionId, actualTopicId, preventNavigation, isWorksheet, isTutorPreview]);

    const isQuizContinuation = quizQuestions.length < allQuestionAttempts.length;
    // Once a day (server rule, earnedToday.js): this go earns no Sparks because another already did today.
    const isHomeLessonQuiz = quizSubmission?.track === 'home' && !quizSubmission.isHomework && !quizSubmission.isCoreSkill && !quizSubmission.isPreQuiz;
    const sparksRepeat = !!quizSubmission && (isWorksheet
        ? isWorksheetRepeat(earnedToday, quizSubmission._id)
        : isHomeLessonQuiz && isHomeRepeat(earnedToday, quizSubmission.lessonId, quizSubmission._id));

    // Helper function to strip populated data before sending PATCH requests (quiz)
    const stripPopulatedData = (quizSubmissionData) => {
        return {
            _id: quizSubmissionData._id,
            lessonId: quizSubmissionData.lessonId,
            topicId: quizSubmissionData.topicId,
            childId: quizSubmissionData.childId,
            userId: quizSubmissionData.userId,
            QuizStatus: quizSubmissionData.QuizStatus,
            NGLFeedbackStatus: quizSubmissionData.NGLFeedbackStatus,
            AttemptNumber: quizSubmissionData.AttemptNumber,
            isHomework: quizSubmissionData.isHomework,
            completionDate: quizSubmissionData.completionDate,
            createdAt: quizSubmissionData.createdAt,
            updatedAt: quizSubmissionData.updatedAt,
            timerEnabled: quizSubmissionData.timerEnabled,
            remainingSeconds: quizSubmissionData.remainingSeconds,
            questionAttempts: quizSubmissionData.questionAttempts.map(attempt => ({
                _id: attempt._id,
                LessonQuestionId: typeof attempt.LessonQuestionId === 'object'
                    ? attempt.LessonQuestionId._id
                    : attempt.LessonQuestionId,
                QuestionStatus: attempt.QuestionStatus,
                chosenAnswer: attempt.chosenAnswer,
                Feedback: attempt.Feedback,
                BoxTypeAnswers: attempt.BoxTypeAnswers,
                explanationQuestionId: typeof attempt.explanationQuestionId === 'object'
                    ? attempt.explanationQuestionId._id
                    : attempt.explanationQuestionId,
                explanationChosenAnswer: attempt.explanationChosenAnswer,
                explanationResponseStatus: attempt.explanationResponseStatus,
                explanationBoxTypeAnswers: attempt.explanationBoxTypeAnswers
            }))
        };
    };

    // Strip worksheet submission for PATCH (only send fields the API accepts)
    const stripWorksheetData = (data) => {
        const out = {
            questionAttempts: (data.questionAttempts || []).map(attempt => ({
                LessonQuestionId: typeof attempt.LessonQuestionId === 'object' && attempt.LessonQuestionId
                    ? attempt.LessonQuestionId._id
                    : attempt.LessonQuestionId,
                lessonId: typeof attempt.lessonId === 'object' && attempt.lessonId
                    ? attempt.lessonId._id || attempt.lessonId
                    : attempt.lessonId,
                QuestionStatus: attempt.QuestionStatus,
                chosenAnswer: attempt.chosenAnswer || '',
                Feedback: attempt.Feedback || '',
                BoxTypeAnswers: attempt.BoxTypeAnswers,
                explanationQuestionId: attempt.explanationQuestionId && (typeof attempt.explanationQuestionId === 'object'
                    ? attempt.explanationQuestionId._id
                    : attempt.explanationQuestionId),
                explanationChosenAnswer: attempt.explanationChosenAnswer,
                explanationResponseStatus: attempt.explanationResponseStatus,
                explanationBoxTypeAnswers: attempt.explanationBoxTypeAnswers
            }))
        };
        if (data.QuizStatus != null) out.QuizStatus = data.QuizStatus;
        if (data.completionDate != null) out.completionDate = data.completionDate;
        if (data.remainingSeconds != null) out.remainingSeconds = data.remainingSeconds;
        return out;
    };

    const handleStartQuiz = () => {
        setQuizStarted(true);

        if (quizQuestions.length === 0) {
            // All questions already answered (e.g. resuming a Pending submission where
            // every attempt was saved but QuizStatus never flipped to Complete).
            // Pass the existing attempts so we don't overwrite them with an empty array.
            completeQuiz(quizSubmission.questionAttempts);
        }
    };

    const handleAnswerSubmit = async (isCorrect, selectedAnswerId, boxSubmission) => {
        setButtonsAreClickable(false);
        const currentQuestionId = quizQuestions[currentQuestionIndex];

        // Current question (already populated with its explanation if it exists)
        const currentQuestion = quizQuestions[currentQuestionIndex];

        // Prepare the updated question attempt
        const updatedQuestionAttempts = quizSubmission.questionAttempts.map(attempt => {
            if (attempt.LessonQuestionId._id === currentQuestionId._id) {
                return {
                    ...attempt,
                    QuestionStatus: isCorrect ? 'correct' : 'incorrect',
                    chosenAnswer: selectedAnswerId,
                    // Only include BoxTypeAnswers if boxSubmission is provided
                    ...(boxSubmission && { BoxTypeAnswers: boxSubmission })
                };
            }
            return attempt;
        });

        // If answer is incorrect and there's an explanation question (skip when worksheet + timer)
        const showExplanation = !isCorrect && currentQuestion.answerExplanationQuestionId
            && !(isWorksheet && quizSubmission.timerEnabled);

        posthog.capture('answer_submitted', {
            questionId: currentQuestion?._id,
            questionType: currentQuestion?.questionType,
            questionIndex: currentQuestionIndex,
            isCorrect,
            showingExplanation: showExplanation,
            quizSubmissionId: quizSubmission?._id,
        });

        if (showExplanation) {
            const updatedQuizSubmission = {
                ...quizSubmission,
                questionAttempts: updatedQuestionAttempts
            };

            // Persist the attempt before showing the explanation so the record is
            // never lost if the student closes the window or the page crashes.
            setQuizSubmission(updatedQuizSubmission);
            const strippedData = isWorksheet ? stripWorksheetData(updatedQuizSubmission) : stripPopulatedData(updatedQuizSubmission);
            const answerResponse = await patch(
                isWorksheet ? `/worksheets/${quizSubmission._id}` : `/quiz/quiz-submissions/${quizSubmission._id}`,
                strippedData,
                true
            );
            pushFromResponse(answerResponse);

            setShowNextButton(false);
            setShowFeedback(true);

            // Set a timeout to show the explanation question
            setTimeout(() => {
                // Hide feedback when showing explanation
                setShowFeedback(false);

                // Trigger feedback reset in QuestionPage
                setResetFeedback(prev => !prev); // Toggle to trigger effect

                // Set the currentExplanationQuestion first to ensure
                // the QuestionPage receives the new question
                setCurrentExplanationQuestion(currentQuestion.answerExplanationQuestionId);

                // Set a small timeout before enabling explanation mode to allow
                // input fields in QuestionPage to reset properly
                setTimeout(() => {
                    setShowingExplanation(true);
                    setButtonsAreClickable(true); // Enable interaction with explanation question
                }, 50);
            }, 2000);
        } else {
            // No explanation or correct answer (or timed worksheet: no explanation), update the database immediately
            const updatedQuizSubmission = {
                ...quizSubmission,
                questionAttempts: updatedQuestionAttempts
            };

            setQuizSubmission(updatedQuizSubmission);
            const strippedData = isWorksheet ? stripWorksheetData(updatedQuizSubmission) : stripPopulatedData(updatedQuizSubmission);
            const answerResponse = await patch(
                isWorksheet ? `/worksheets/${quizSubmission._id}` : `/quiz/quiz-submissions/${quizSubmission._id}`,
                strippedData,
                true
            );
            pushFromResponse(answerResponse);

            setShowNextButton(true);
            setShowFeedback(true);

            if (currentQuestionIndex === quizQuestions.length - 1) {
                await completeQuiz(updatedQuestionAttempts);
            } else {
                setButtonsAreClickable(true);
            }
        }
    };

    // Handle explanation question answers
    const handleExplanationSubmit = async (isCorrect, selectedAnswerId, boxSubmission) => {
        setButtonsAreClickable(false);

        const currentQuestionId = quizQuestions[currentQuestionIndex];

        // Update the current question attempt with both original answer and explanation data
        const updatedQuestionAttempts = quizSubmission.questionAttempts.map(attempt => {
            if (attempt.LessonQuestionId._id === currentQuestionId._id) {
                return {
                    ...attempt,
                    explanationQuestionId: currentExplanationQuestion._id,
                    explanationChosenAnswer: selectedAnswerId,
                    explanationResponseStatus: isCorrect ? 'correct' : 'incorrect',
                    // Only include explanation box answers if provided
                    ...(boxSubmission && { explanationBoxTypeAnswers: boxSubmission })
                };
            }
            return attempt;
        });

        // Now update the quiz submission with both the original answer and the explanation answer
        const updatedQuizSubmission = {
            ...quizSubmission,
            questionAttempts: updatedQuestionAttempts
        };

        setQuizSubmission(updatedQuizSubmission);

        const strippedData = isWorksheet ? stripWorksheetData(updatedQuizSubmission) : stripPopulatedData(updatedQuizSubmission);
        const explanationResponse = await patch(
            isWorksheet ? `/worksheets/${quizSubmission._id}` : `/quiz/quiz-submissions/${quizSubmission._id}`,
            strippedData,
            true
        );
        pushFromResponse(explanationResponse);

        // Reset explanation state - MOVED TO NEXT BUTTON CLICK
        // setShowingExplanation(false);
        // setCurrentExplanationQuestion(null);

        // Show feedback and next button after answering explanation
        setShowFeedback(true);
        setShowNextButton(true);

        // Check if this was the last question
        if (currentQuestionIndex === quizQuestions.length - 1) {
            await completeQuiz(updatedQuestionAttempts);
        } else {
            // Enable next button for moving to next question
            setButtonsAreClickable(true);
        }
    };

    // Add a handler for the Next button click
    const handleNextClick = () => {
        // Reset explanation state when moving to next question
        setShowingExplanation(false);
        setCurrentExplanationQuestion(null);
        setShowNextButton(false);
        setShowFeedback(false);

        // Reset button state for next question
        setButtonsAreClickable(true);
    };

    const completeQuiz = async (questionAttempts) => {
        const finalQuizSubmission = {
            ...quizSubmission,
            QuizStatus: "Complete",
            questionAttempts: questionAttempts,
            completionDate: new Date()
        };

        setQuizSubmission(finalQuizSubmission);
        const strippedFinalData = isWorksheet ? stripWorksheetData(finalQuizSubmission) : stripPopulatedData(finalQuizSubmission);
        const patchResponse = await patch(
            isWorksheet ? `/worksheets/${quizSubmission._id}` : `/quiz/quiz-submissions/${quizSubmission._id}`,
            strippedFinalData,
            true
        );
        pushFromResponse(patchResponse);

        posthog.capture('quiz_completed', {
            quizSubmissionId: quizSubmission?._id,
            totalQuestions: questionAttempts.length,
            correctCount: questionAttempts.filter(a => a.QuestionStatus === 'correct').length,
            isWorksheet,
        });

        refreshStreak();

        if (preventNavigation && onQuizComplete) {
            // Pass forceGateNow to the parent so it can trigger red gate flow
            onQuizComplete(finalQuizSubmission, patchResponse?.forceGateNow);
        } else if (!isWorksheet) {
            if (!isCoreSkill) {
                try {
                    await put(`/children/${loggedInChild._id}/current-home-topic`, { topicId: null }, true);
                    await del(`/childTopics/${loggedInChild._id}/${actualTopicId}?track=${quizSubmission?.track}`, true);
                } catch (error) {
                    console.error('Error clearing current topic or deleting ChildTopic entry:', error);
                }
            } else if (quizSubmission?.isPreQuiz) {
                await recordPreQuizResult(questionAttempts);
            } else {
                markCoreSkillCompleted();
            }
        }
    };

    const calculatePreQuizScore = (attempts) => {
        if (!attempts.length) return 0;
        const correct = attempts.filter(a => a.QuestionStatus === 'correct').length;
        return Math.round((correct / attempts.length) * 100);
    };

    const recordPreQuizResult = async (attempts) => {
        const score = calculatePreQuizScore(attempts);
        let capReached = false;
        try {
            const updatedChild = await put(`/children/${loggedInChild._id}/core-skill-prequiz`, { score }, true);
            pushFromResponse(updatedChild);
            // If the score was 100 but the backend did not mark it as a pre-quiz pass,
            // the daily streak cap was reached — treat this like a full-lesson completion cycle.
            if (score === 100 && updatedChild && updatedChild.coreSkillsPreQuizPassed === false) {
                capReached = true;
            }
            refreshStreak();
        } catch (error) {
            console.error('Error recording pre-quiz result:', error);
        }
        setPreQuizResult({ score, passed: score === 100 && !capReached, capReached });
    };

    const markCoreSkillCompleted = async () => {
        try {
            if (coreSkillSource === 'dashboard_progress') {
                if (dashboardSessionId && loggedInChild?._id) {
                    await completeCoreSkillSession(loggedInChild._id, dashboardSessionId);
                }
            } else {
                await put(`/children/${loggedInChild._id}/complete-current-core-skill`, {}, true);
            }
        } catch (error) {
            console.error('Error marking core skill as completed:', error);
        }
    };

    // Safety net: if the index ever runs past the question list, re-anchor on the submission's
    // own attempts instead of rendering no question: finish when every one is answered, else
    // resume at the first unanswered.
    const positionLost = questionActive && !currentQuestion;
    useEffect(() => {
        if (!positionLost) return;
        const attempts = quizSubmission.questionAttempts;
        const unanswered = attempts
            .filter(attempt => attempt.QuestionStatus === 'not attempted')
            .map(attempt => attempt.LessonQuestionId);
        posthog.capture('quiz_position_lost', {
            quizSubmissionId: quizSubmission._id,
            questionIndex: currentQuestionIndex,
            questionsLength: quizQuestions.length,
            unanswered: unanswered.length,
        });
        if (unanswered.length === 0) {
            completeQuiz(attempts);
        } else {
            setQuizQuestions(unanswered);
            setCurrentQuestionIndex(0);
        }
    }, [positionLost]);

    // Timer: countdown (worksheet and pre-quiz)
    const timerExpiredRef = useRef(false);
    useEffect(() => {
        if (!quizSubmission?.timerEnabled || !quizStarted || remainingSeconds == null) return;
        const t = setInterval(() => {
            setRemainingSeconds((prev) => {
                if (prev == null || prev <= 1) return 0;
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [quizSubmission?.timerEnabled, quizStarted]);

    useEffect(() => {
        if (!quizSubmission?.timerEnabled || !quizStarted || remainingSeconds !== 0) return;
        if (timerExpiredRef.current) return;
        timerExpiredRef.current = true;

        const handleTimerExpiry = async () => {
            const current = quizSubmission;
            const finalAttempts = (current.questionAttempts || []).map((a) => ({
                ...a,
                QuestionStatus: a.QuestionStatus === 'not attempted' ? 'not attempted' : a.QuestionStatus
            }));
            const finalSubmission = {
                ...current,
                QuizStatus: 'Complete',
                completionDate: new Date(),
                remainingSeconds: 0,
                questionAttempts: finalAttempts
            };
            setQuizSubmission(finalSubmission);
            try {
                const expiryResponse = isWorksheet
                    ? await patch(`/worksheets/${current._id}`, stripWorksheetData(finalSubmission), true)
                    : await patch(`/quiz/quiz-submissions/${current._id}`, stripPopulatedData(finalSubmission), true);
                pushFromResponse(expiryResponse);
                refreshStreak(); // a timed-out worksheet is a finish too, like completeQuiz
            } catch (err) {
                console.error('Error saving timer expiry:', err);
            }
            if (current?.isPreQuiz && loggedInChild) {
                await recordPreQuizResult(finalAttempts);
            }
            if (onQuizComplete) onQuizComplete(finalSubmission);
        };
        handleTimerExpiry();
    }, [quizSubmission?.timerEnabled, quizSubmission?.questionAttempts, quizStarted, remainingSeconds]);

    const remainingSecondsRef = useRef(remainingSeconds);
    remainingSecondsRef.current = remainingSeconds;
    const quizSubmissionRef = useRef(quizSubmission);
    quizSubmissionRef.current = quizSubmission;
    const onTimerSyncRef = useRef(onTimerSync);
    onTimerSyncRef.current = onTimerSync;

    // Timer: send the countdown to the classroom once when it becomes active,
    // then every 30s alongside the persist interval below. remainingSeconds is
    // deliberately read via ref and kept out of the deps — putting it in the
    // deps would broadcast a Daily app-message every second.
    useEffect(() => {
        if (!quizSubmission?.timerEnabled || !quizStarted) return;
        if (remainingSecondsRef.current == null) return;
        onTimerSyncRef.current?.(remainingSecondsRef.current);
    }, [quizSubmission?.timerEnabled, quizStarted]);

    // Timer: persist remainingSeconds every 30s (worksheet and pre-quiz)
    useEffect(() => {
        if (!quizSubmission?.timerEnabled || !quizStarted || remainingSeconds == null) return;
        const submissionId = quizSubmission._id;
        const id = setInterval(async () => {
            const secs = remainingSecondsRef.current;
            if (secs == null || secs <= 0) return;
            onTimerSyncRef.current?.(secs);
            try {
                if (isWorksheet) {
                    await patch(`/worksheets/${submissionId}`, { remainingSeconds: secs }, true);
                } else {
                    const sub = quizSubmissionRef.current;
                    if (sub) {
                        await patch(
                            `/quiz/quiz-submissions/${submissionId}`,
                            stripPopulatedData({ ...sub, remainingSeconds: secs }),
                            true
                        );
                    }
                }
            } catch (err) {
                console.error('Error saving timer:', err);
            }
        }, 30000);
        return () => clearInterval(id);
    }, [quizSubmission?.timerEnabled, quizSubmission?._id, quizStarted]);

    // commenting out music for now so it doesn't annoy me while I'm building the student classroom

    /*   useEffect(() => {
           let audio;
           if (quizStarted) {
               getRandomMusicFile().then(file => {
                   setMusicFile(file);
                   audio = new Audio(file);
                   audio.loop = true;
                   audio.play();
               });
           }
           return () => {
               if (audio) {
                   audio.pause();
                   audio.currentTime = 0;
               }
           };
       }, [quizStarted]); */

    // remove the params and dependencies that i don't need at some point
    useEffect(() => {
        if (isTutorPreview || !onStateChange) return;
        const currentQ = quizQuestions[currentQuestionIndex];
        const currentQuestionId = currentQ && (typeof currentQ === 'object' ? currentQ._id : currentQ);
        onStateChange({
            quizSubmission,
            currentQuestionIndex,
            currentQuestionId,
            totalQuestions,
            isQuizMode: true,
            quizStarted,
            quizStatus: quizSubmission?.QuizStatus,
            showingExplanation,
            currentExplanationQuestion
        });
    }, [
        currentQuestionIndex,
        totalQuestions,
        quizStarted,
        quizSubmission,
        onStateChange,
        showingExplanation,
        currentExplanationQuestion,
        isTutorPreview,
        quizQuestions
    ]);

    if (loading && !isTutorPreview) {
        return <div>Loading...</div>;
    }

    return (
        <div className={`quiz-container${inClassroom ? ' quiz-container--in-classroom' : ''}${quizSubmission?.QuizStatus === 'Complete' ? ' quiz-container--complete' : ''}`}>
            {!quizStarted ? (
                <div className="start-container">
                    <h2>
                        {isQuizContinuation
                            ? 'Are you ready to continue the practice questions?'
                            : 'Are you ready to start the practice questions?'}
                    </h2>
                    {sparksRepeat && (
                        <p className="mx-auto mb-4 max-w-md rounded-lg bg-[#F0EDFA] px-4 py-2 text-sm font-medium text-[#2F1E6B]" data-testid="sparks-repeat-start">
                            {isWorksheet ? REPEAT_COPY.worksheetStart : REPEAT_COPY.homeStart}
                        </p>
                    )}
                    <button
                        onClick={handleStartQuiz}
                        disabled={isTutorPreview}
                        className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded transition duration-300 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {isQuizContinuation ? 'Continue Practice Questions' : 'Start Practice Questions'}
                    </button>
                </div>
            ) : (
                <>
                    {quizSubmission?.timerEnabled && remainingSeconds != null && remainingSeconds >= 0 && quizSubmission.QuizStatus !== 'Complete' && (
                        <div className="sticky top-0 z-10 bg-amber-100 border-b border-amber-300 px-4 py-2 text-center text-sm font-medium text-amber-900">
                            Time remaining: {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, '0')}
                        </div>
                    )}
                    {quizSubmission && quizSubmission.QuizStatus === 'Complete' ? (
                        <div className="completion-message">
                            {preQuizResult ? (
                                preQuizResult.capReached || preQuizResult.passed ? (
                                    <LessonRecap
                                        continueLabel="Back to Core Skills"
                                        onContinue={() => navigate('/core-skills')}
                                        detail={preQuizResult.capReached
                                            ? "You've completed the maximum number of pre-quizzes. Your next Core Skills lesson will be released in 48 hours."
                                            : "You've already mastered this topic — on to the next one!"}
                                        showShop
                                    >
                                        {preQuizResult.capReached ? 'Excellent! You scored 100%!' : 'Excellent! You scored 100% on the pre-quiz!'}
                                    </LessonRecap>
                                ) : (
                                    // The child goes on to the full lesson, so the tally stays open for its recap.
                                    <>
                                        <h2>You scored {preQuizResult.score}%.</h2>
                                        <p>Let&apos;s work through the full lesson to master this topic.</p>
                                        <SparksStarsUpdate />
                                        <button
                                            onClick={() => navigate('/core-skills')}
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded mt-4"
                                        >
                                            Go to Core Skills
                                        </button>
                                    </>
                                )
                            ) : !isWorksheet && !preventNavigation && !isTutorPreview && !quizSubmission?.isPreQuiz ? (
                                // In a classroom (preventNavigation) the session's own end screen shows the recap.
                                <LessonRecap
                                    continueLabel={quizSubmission?.isHomework ? 'Back to Assignments' : isCoreSkill ? 'Back to Core Skills' : 'Back to Home Learning'}
                                    onContinue={() => navigate(quizSubmission?.isHomework ? '/homework' : isCoreSkill ? '/core-skills' : '/lets-learn')}
                                    detail={sparksRepeat ? REPEAT_COPY.homeFinish : undefined}
                                    showShop
                                >
                                    {quizFinishedMessage && totalQuestions === 0 ? 'No questions to complete for now.' : "You've finished the lesson!"}
                                </LessonRecap>
                            ) : (
                                <>
                                    {preventNavigation && onQuizComplete && !isTutorPreview && <SparksFinishHold />}
                                    {quizFinishedMessage && totalQuestions === 0 && (
                                        <h2>No questions to complete for now.</h2>
                                    )}
                                    <h2>
                                        {isWorksheet
                                            ? "You have finished the worksheet!"
                                            : `You have finished the lesson! Click on ${isCoreSkill ? "Core Skills!" : "Home Learning!"} at the top of the page to select a new lesson !`}
                                    </h2>
                                </>
                            )}
                        </div>
                    ) : (
                        currentQuestion && (
                          <MaybeZoom enabled={!isTutorPreview} immersive onExit={inClassroom ? undefined : handleExit}>
                            <QuestionPage
                                showSparks={!isTutorPreview}
                                allQuizQuestionsLength={totalQuestions}
                                outstandingQuestionsLength={quizQuestions.length}
                                currentQuestionNumber={isTutorPreview ? currentQuestionIndex + 1 : undefined}
                                setCurrentQuestionIndex={setCurrentQuestionIndex}
                                quizQuestion={currentQuestion}
                                onAnswerSelect={showingExplanation ? handleExplanationSubmit : handleAnswerSubmit}
                                ButtonsAreClickable={isTutorPreview ? false : ButtonsAreClickable}
                                setButtonsAreClickable={setButtonsAreClickable}
                                onInputChange={onInputChange}
                                isExplanationQuestion={showingExplanation}
                                showNextButton={showNextButton}
                                showFeedback={showFeedback}
                                onNextClick={handleNextClick}
                                resetFeedback={resetFeedback}
                                hideBlankBoxCorrectnessIndicators={isCoreSkill && !!quizSubmission?.isPreQuiz}
                            />
                          </MaybeZoom>
                        )
                    )}
                </>
            )}
        </div>
    );
};

export default QuizPage;
